<template>
    <v-container>
      <v-btn @click="$router.push('/')" icon="mdi-arrow-left" class="mb-4"></v-btn>
      <h1>Add New Client</h1>
      <v-form @submit.prevent="submitForm" ref="form">
        <v-text-field
          v-model="clientRequest.name"
          label="Client Name"
          required
          :error-messages="clientNameError"
          @input="clearErrors"
        ></v-text-field>
        <v-text-field
          v-model="clientRequest.contact.name"
          label="Contact Name"
          required
          :error-messages="contactNameError"
          @input="clearErrors"
        ></v-text-field>
        <v-text-field
          v-model="clientRequest.contact.email"
          label="Contact Email"
          required
          type="email"
          :error-messages="contactEmailError"
          @input="clearErrors"
        ></v-text-field>
        <v-btn 
          type="submit" 
          color="primary" 
          :loading="loading"
          :disabled="loading"
        >
          Add Client
        </v-btn>
      </v-form>
      <ClientAddedModal
        v-model="showUriModal"
        :client-id="clientId"
      />
    </v-container>
</template>
  
<script setup lang="ts">
import { ref, reactive, watch, inject } from 'vue';
import { api } from '@/services/api';
import type ClientRequest from '@/models/clientRequest';
import ClientAddedModal from '@/components/ClientAddedModal.vue';

// Inject the refresh method
const refreshClients = inject('refreshClients') as () => Promise<void>;

const clientRequest = reactive<ClientRequest>({
  name: '',
  contact: {
    name: '',
    email: ''
  }
});

const clientId = ref('');
const loading = ref(false);
const clientNameError = ref('');
const contactNameError = ref('');
const contactEmailError = ref('');
const showUriModal = ref(false);
const form = ref();

const clearErrors = () => {
  clientNameError.value = '';
  contactNameError.value = '';
  contactEmailError.value = '';
};

const submitForm = async () => {
  if (!clientRequest.name.trim()) {
    clientNameError.value = 'Client name is required';
    return;
  }

  loading.value = true;
  clearErrors();

  try {
    clientId.value = await api.addClient(clientRequest);
    if (clientId.value) {
      console.log(clientId.value + ' is the client id as returned from the api');
      
      // Refresh the client list after successful addition
      await refreshClients();
      
      showUriModal.value = true;
    }

    loading.value = false;
    
    // Wait for modal to close before resetting form
    await new Promise(resolve => {
      const unwatch = watch(showUriModal, (newValue) => {
        if (!newValue) {
          unwatch();
          resolve(true);
        }
      });
    });

    // Reset form after modal is closed
    Object.assign(clientRequest, {
      name: '',
      id: '',
      contact: {
        name: '',
        email: ''
      }
    });
  } catch (error) {
    clientNameError.value = 'Failed to add client. Please try again.';
    console.error('Error adding client:', error);
  } finally {
    loading.value = false;
  }
};
</script>